import { Searchbar } from "@components/header/searchbar";
import { Stack } from "@mui/material";
import { Text } from "@flat-ui";
import { ReactNode } from "react";
import { useMediaQueryList } from "@hooks/use-media-query";
import { LoadingWrapper } from "@context-providers/loading-state-context";

interface Props {
  /** Buttons shown in table action bar on the left side */
  sortActions?: ReactNode;

  /** Visible number of rows */
  numberOfRows: number;

  /** Optional total number of rows for labels like "11 of 120 projects" */
  totalNumberOfRows?: number;

  /** Export component to show in the toolbar */
  exportComponent?: ReactNode;

  /** Display name for the entity type in the toolbar (e.g., "Projects", "Members") */
  toolbarEntityName: string;

  /** Whether data is loading - automatically handles loading state */
  isLoading?: boolean;

  /** Whether to show the rows count label (useful to disable for server-side pagination) */
  shouldShowRowsLabel?: boolean;

  /**
   * When true, skips the route-specific input validation (e.g. minimum character check).
   */
  shouldSkipValidation?: boolean;

  /**
   * When true, forces the compact stacked layout (search, then sort/export) regardless of
   * screen size. Useful when the toolbar is rendered inside a narrow column.
   */
  shouldStack?: boolean;

  /**
   * When true, removes the toolbar's top margin so its first row (the search bar)
   * aligns flush with the top of adjacent content (e.g. the map in the map view).
   */
  shouldRemoveTopMargin?: boolean;
}

/** Toolbar to show on top of page that displays entities */
export function EntitiesToolbar({
  sortActions,
  numberOfRows,
  totalNumberOfRows,
  exportComponent,
  toolbarEntityName,
  isLoading = false,
  shouldShowRowsLabel = true,
  shouldSkipValidation = false,
  shouldStack = false,
  shouldRemoveTopMargin = false,
}: Props): JSX.Element {
  const { isScreenSmOrSmaller } = useMediaQueryList();
  const isCompact = isScreenSmOrSmaller || shouldStack;
  const defaultTopMargin = isScreenSmOrSmaller ? "1rem" : "1.25rem";
  const topMargin = shouldRemoveTopMargin ? 0 : defaultTopMargin;
  const hasTotalRows = totalNumberOfRows !== undefined;
  const shouldRenderRowsLabel =
    shouldShowRowsLabel && (hasTotalRows || numberOfRows > 0);

  const entityLabel =
    (hasTotalRows ? totalNumberOfRows : numberOfRows) === 1
      ? toolbarEntityName
      : `${toolbarEntityName}s`;

  const rowsLabel = shouldRenderRowsLabel && (
    <Text variant="bodyS" component="var">
      {hasTotalRows
        ? `${numberOfRows} of ${totalNumberOfRows} ${entityLabel}`
        : `${numberOfRows} ${entityLabel}`}
    </Text>
  );

  const sortActionsWrapper = sortActions && (
    <LoadingWrapper isLoading={isLoading}>
      <Stack flex={1}>{sortActions}</Stack>
    </LoadingWrapper>
  );

  return (
    <Stack
      mt={topMargin}
      mb={"1.25rem"}
      sx={{
        flexDirection: isCompact ? "column" : "row",
        justifyContent: isCompact ? "flex-start" : "space-between",
        alignItems: isCompact ? "stretch" : "start",
      }}
    >
      {/* Left section / Top section in mobile */}
      <Stack
        direction={isCompact ? "column" : "row"}
        alignItems={isCompact ? "stretch" : "start"}
        gap={isCompact ? "0.75rem" : 0}
      >
        {/* Search - full width on mobile */}
        <Searchbar
          shouldFullWidth={isCompact}
          shouldSkipValidation={shouldSkipValidation}
        />

        {/* Sort and Export row on mobile, sort only on desktop */}
        {isCompact ? (
          // Compact: sort on the left, row count next to export on the right (like the list view)
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            {sortActionsWrapper}
            <Stack direction="row" alignItems="center" gap={2}>
              {rowsLabel}
              {exportComponent}
            </Stack>
          </Stack>
        ) : (
          // Desktop: Sort only (export goes to right section)
          sortActionsWrapper
        )}
      </Stack>

      {/* Right section - Desktop */}
      {!isCompact && (
        <Stack direction="row" alignItems="center" gap={2}>
          {rowsLabel}
          {exportComponent}
        </Stack>
      )}
    </Stack>
  );
}
